import { useEffect } from "react"
import { router } from "@inertiajs/react"

export interface CartItem {
  id: number
  name: string
  price: string
  quantity: number
  image_url: string | null
  remove_url: string
}

export interface CartData {
  title: string
  empty_label: string
  quantity_label_template: string
  remove_label: string
  total_label: string
  shipping_note: string
  payment_button_label: string
  checkout_url: string
  count: number
  total: string
  items: CartItem[]
  auto_open: boolean
}

interface CartProps {
  open: boolean
  onClose: () => void
  cart: CartData
}

export default function Cart({ open, onClose, cart }: CartProps) {
  useEffect(() => {
    if (!open) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }

    const original = document.body.style.overflow
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", handleEscape)

    return () => {
      document.body.style.overflow = original
      document.removeEventListener("keydown", handleEscape)
    }
  }, [open, onClose])

  if (!open) return null

  const handleRemove = (url: string) => {
    router.delete(url, { preserveScroll: true })
  }

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cart-slide-over-title"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close cart"
        className="absolute inset-0 bg-midgray bg-opacity-75 transition-opacity"
      />

      <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-md">
          <div className="flex h-full flex-col overflow-y-scroll bg-contrast shadow-xl">
            <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 bg-light">
              <div className="flex items-start justify-between">
                <h2
                  id="cart-slide-over-title"
                  className="text-lg font-medium text-contrast"
                >
                  {cart.title}
                </h2>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="ml-3 flex h-7 items-center cursor-pointer"
                >
                  <span className="w-16 h-16 flex items-center justify-center rounded-full p-3">
                    <i className="fa-solid fa-xmark ml-6 text-2xl text-contrast" />
                  </span>
                </button>
              </div>

              <div className="mt-8">
                {cart.items.length === 0 ? (
                  <p className="text-content text-sm">{cart.empty_label}</p>
                ) : (
                  <ul role="list" className="-my-6 divide-y divide-content">
                    {cart.items.map((item) => (
                      <CartLine
                        key={item.id}
                        item={item}
                        quantityLabelTemplate={cart.quantity_label_template}
                        removeLabel={cart.remove_label}
                        onRemove={handleRemove}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {cart.items.length > 0 && (
              <div className="border-t border-content px-4 py-6 sm:px-6 bg-light">
                <div className="flex justify-between text-base font-medium text-contrast">
                  <p>{cart.total_label}</p>
                  <p>{cart.total}</p>
                </div>
                <p className="mt-0.5 text-sm text-midgray">
                  {cart.shipping_note}
                </p>
                <div className="mt-6">
                  <a
                    href={cart.checkout_url}
                    data-turbo="false"
                    className="flex items-center justify-center rounded-md border border-transparent bg-primary/90 px-6 py-3 text-base font-medium text-light shadow-sm hover:bg-primary"
                  >
                    {cart.payment_button_label}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function CartLine({
  item,
  quantityLabelTemplate,
  removeLabel,
  onRemove,
}: {
  item: CartItem
  quantityLabelTemplate: string
  removeLabel: string
  onRemove: (url: string) => void
}) {
  const quantityLabel = quantityLabelTemplate.replace("{count}", String(item.quantity))

  return (
    <li className="flex py-6">
      <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-md border border-content">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="h-full w-full object-cover object-center"
          />
        ) : (
          <div className="h-full w-full bg-midgray flex items-center justify-center">
            <i className="fa-solid fa-image text-content text-2xl" />
          </div>
        )}
      </div>
      <div className="ml-4 flex flex-1 flex-col">
        <div>
          <div className="flex justify-between text-base font-medium text-contrast">
            <h3>{item.name}</h3>
            <p className="ml-3">{item.price}</p>
          </div>
        </div>
        <div className="flex flex-1 items-end justify-between text-sm">
          <p className="text-midgray">{quantityLabel}</p>
          <button
            type="button"
            onClick={() => onRemove(item.remove_url)}
            className="font-medium text-primary hover:text-primary/90 cursor-pointer"
          >
            {removeLabel}
          </button>
        </div>
      </div>
    </li>
  )
}
